import type { ThreatEventCandidateItem, ThreatEventItem } from '../api-utils/threats';

/** An incident as the campaign screen's remediation agent reads it. */
export interface Incident {
  id: string;
  source: 'aws' | 'crowdstrike' | 'okta';
  type: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  timestamp: Date;
  status: 'detected' | 'analyzing' | 'remediated';
  resource?: string;
  mitre?: {
    tactic: string;
    technique: string;
    id: string;
  };
  raw?: any;
}

const SEVERITIES: Incident['severity'][] = ['critical', 'high', 'medium', 'low'];

function text(value: unknown): string {
  return String(value ?? '').trim();
}

function severityOf(value: unknown): Incident['severity'] {
  const severity = text(value).toLowerCase() as Incident['severity'];
  return SEVERITIES.includes(severity) ? severity : 'low';
}

function statusOf(status: ThreatEventItem['detection_status']): Incident['status'] {
  if (status === 'remediated') return 'remediated';
  if (status === 'in_progress') return 'analyzing';
  return 'detected';
}

function sourceOf(event: ThreatEventItem, candidate: ThreatEventCandidateItem | null): Incident['source'] {
  if (text(candidate?.source || event.provider).toLowerCase() === 'okta') return 'okta';
  if (text(candidate?.source).toLowerCase().includes('crowd')) return 'crowdstrike';
  return 'aws';
}

/** The incident for a confirmed threat event and the candidate it came from, if known. */
export function incidentFromThreatEvent(
  event: ThreatEventItem,
  candidate: ThreatEventCandidateItem | null
): Incident {
  return {
    id: event._id,
    source: sourceOf(event, candidate),
    type: event.detection_classification || 'Threat',
    severity: severityOf(event.detection_risk_level),
    title: text(event.detection_classification) || 'Threat',
    description: text(event.detection_description),
    timestamp: new Date(event.first_event_time || event.created_at || Date.now()),
    status: statusOf(event.detection_status),
    raw: { ...(candidate || {}), threatEvent: event },
  };
}
